// TODO: Migration ot translatedCopy.js of raw strings
// TODO: Using generative voice service to enable generative at mount
import React, { useState, useEffect, useRef } from 'react';
import { getUserData } from '../../services/API';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton2/AmiraIconButton2';
import ImageSelection from '../Global/ImageSelection/ImageSelection';
import cx from 'classnames';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { getCopyByGradeband } from '../../services/ThemingService';
import { MAX_REPLAYS } from '../../constants/assessmentScreenTimings';

const IDLE_ENCOURAGEMENT_TIMEOUT = 6000;
const IDLE_SUBMIT_TIMEOUT = 60000;

// TODO: Final Introduction Model Sources
const INTRODUCTION_MODEL_CORRECT_KEY = "turtle";
const INTRODUCTION_MODEL_OPTIONS = [
  {
    key: "beetle",
    imageUrl: "https://assets.app.amiralearning.com/blending/2024/images/beetle.png"
  },
  {
    key: "towel",
    imageUrl: "https://assets.app.amiralearning.com/blending/2024/images/towel.png"
  },
  {
    key: "turtle",
    imageUrl: "https://assets.app.amiralearning.com/blending/2024/images/turtle.png"
  },
  {
    key: "tourist",
    imageUrl: "https://assets.app.amiralearning.com/blending/2024/images/tourist.png"
  },
]
const INTRODUCTION_MODEL_VIDEO_URL = "https://assets.app.amiralearning.com/blending/2024/turtle_syllable.mp4";

const TIME_BETWEEN_INTRODUCTION_OPTIONS = 1000;

const BlendingSelection = ({
  videoURL,
  speech,
  onDone,
  options,
  isInstruction,
  playIntroduction,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  onImageLoadError,
  onAllImagesFailed
}) => {
  // State
  const [selectedOption, setSelectedOption] = useState(null);
  const [hideVideo, setHideVideo] = useState(false);
  const [hideOptions, setHideOptions] = useState(false);
  const [hideSubmit, setHideSubmit] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoPromiseResolver, setVideoPromiseResolver] = useState(null);
  const [canSelectOption, setCanSelectOption] = useState(false);
  const [animatedOptions, setAnimatedOptions] = useState([]);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const inIntroductionRef = useRef(playIntroduction);

  const BLENDING_SELECTION_COPY = getCopyByGradeband(COPY_STRINGS, 'blendingSelection');

  const extendedIdleEncouragementTimeout = shouldExtendTimeout && timeoutExtensionMultiplier 
    ? Math.round(IDLE_ENCOURAGEMENT_TIMEOUT * timeoutExtensionMultiplier)
    : IDLE_ENCOURAGEMENT_TIMEOUT;

  // Calculate timeout with extension if needed
  const extendedSubmitTimeout = shouldExtendTimeout && timeoutExtensionMultiplier 
    ? Math.round(IDLE_SUBMIT_TIMEOUT * timeoutExtensionMultiplier)
    : IDLE_SUBMIT_TIMEOUT;

  const { resetIdleTimer, clearIdleTimer, idleSpeechPlaying, idleTimeoutExpired } = useIdleTimer(
    speech,
    extendedIdleEncouragementTimeout,
    extendedSubmitTimeout
  );
  const isMountedRef = useIsMounted();

  const playIntroductionSequence = async () => {
    // Initialize
    inIntroductionRef.current = true;

    setCanSelectOption(false);
    setHideVideo(true);
    setHideOptions(true);
    setHideSubmit(true);
    setSubmitEnabled(false);
    clearIdleTimer();

    setHideVideo(false);

    await speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(BLENDING_SELECTION_COPY, "introduction"),
      useGenerative: true
    });

    setHideOptions(false);

    for (const option of INTRODUCTION_MODEL_OPTIONS) {
      await new Promise((resolve) => {
        setTimeout(() => {
          setAnimatedOptions((prev) => [...prev, option.key]);
          speech.speakWithPromise({ textToSpeak: `"${option.key}".`, skipTranslation: getUserData().bilingualMode, useGenerative: true }).finally(resolve);
        }, TIME_BETWEEN_INTRODUCTION_OPTIONS);
      });
    }

    await speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(BLENDING_SELECTION_COPY, "introduce_video"),
      useGenerative: true
    });

    await playVideo();

    await speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(BLENDING_SELECTION_COPY, "select_picture_instructions"),
      useGenerative: true
    });

    setSelectedOption(INTRODUCTION_MODEL_CORRECT_KEY);
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Clear the view
    setHideVideo(true);
    setHideOptions(true);
    setHideSubmit(true);

    await speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(BLENDING_SELECTION_COPY, "your_turn"),
      useGenerative: true
    });

    inIntroductionRef.current = false;

    playInstructions();
  };

  const playVideo = async () => {
    const videoPromise = new Promise((resolve) => {
      setVideoPromiseResolver((prevState) => resolve);
    });
    setVideoPlaying(true);
    await videoPromise;
    setVideoPlaying(false);
    setVideoPromiseResolver(null);
    setVideoPlaying(false);
    if (!inIntroductionRef.current) {
      setCanSelectOption(true);
    }
  };

  const playInstructions = async () => {
    setCanSelectOption(false);
    setHideVideo(false);
    setHideOptions(false);
    setHideSubmit(false);
    setSelectedOption(null);
    setSubmitEnabled(false);
    clearIdleTimer();

    for (const option of options) {
      await new Promise((resolve) => {
        setTimeout(() => {
          setAnimatedOptions((prev) => [...prev, option.key]);
          speech.speakWithPromise({ textToSpeak: `"${option.key}".`, skipTranslation: getUserData().bilingualMode, useGenerative: true }).finally(resolve);
        }, 1000);
      });
    }

    setHideVideo(false);

    await speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(BLENDING_SELECTION_COPY, "your_turn_instructions"),
      useGenerative: true
    });

    await playVideo();

    startInteraction();
  };

  const startInteraction = () => {
    setCanSelectOption(true);
    setHideSubmit(false);

    const idleEncouragementText = getCopyByGradeband(BLENDING_SELECTION_COPY, "idle_encouragement_1");
    resetIdleTimer(idleEncouragementText);
  };

  const handleImageSelection = async (key) => {
    setCanSelectOption(false);
    setSubmitEnabled(false);
    clearIdleTimer();
    setSelectedOption(key);
    await speech.speakWithPromise({
      textToSpeak: `"${key}".`,
      skipTranslation: getUserData().bilingualMode,
      useGenerative: true
    });

    if (!isMountedRef.current) return;
    setCanSelectOption(true);
    setSubmitEnabled(true);

    const idleEncouragementText = getCopyByGradeband(BLENDING_SELECTION_COPY, "idle_encouragement_2");
    resetIdleTimer(idleEncouragementText);
  };

  const handleSubmit = () => {
    clearIdleTimer();
    setSubmitted(true);
    setSubmitEnabled(false);
    setCanSelectOption(false);
    if (onDone) onDone(selectedOption);
  };

  // Effects
  useEffect(() => {
    if (idleSpeechPlaying === undefined) return; // Skip the first render

    setCanSelectOption(!idleSpeechPlaying); // Disable selection while idle speech is playing

    if (idleSpeechPlaying) {
      setSubmitEnabled(false);
    } else if (selectedOption) {
      // If idleSpeechPlaying is false and the user has made a selection, then we should re-enable the submit button
      setSubmitEnabled(true);
    }
  }, [idleSpeechPlaying])

  useEffect(() => {
    if (idleTimeoutExpired) {
      setSubmitted(true);
      setCanSelectOption(false);
      setSubmitEnabled(false);

      speech.speakWithPromise({
        textToSpeak: getCopyByGradeband(BLENDING_SELECTION_COPY, "idle_timeout_message")
      }).then(() => {
        handleSubmit();
      });
    }
  }, [idleTimeoutExpired]);

  useEffect(() => {
    if (!isInstruction) {
      if (playIntroduction) {
        playIntroductionSequence();
      } else {
        playInstructions();
      }
    }
  }, [isInstruction, videoURL]);

  // Render
  return (
    <div className="blendingSelection">
      {!hideVideo && (
        <VideoPlayer
          videoURL={inIntroductionRef.current ? INTRODUCTION_MODEL_VIDEO_URL : videoURL}
          text={"Blending Selection Video"}
          unmountWhenDone={getUserData().sessionData.isiPad}
          key="theOnlyBlendingSelectionVideo"
          speech={speech}
          assessment={true}
          videoPlaying={videoPlaying}
          maxReplays={inIntroductionRef.current || submitted ? 0 : MAX_REPLAYS}
          onStart={() => {
            clearIdleTimer();
            setCanSelectOption(false);
            setSubmitEnabled(false);
          }}
          onDone={() => {
            if (videoPromiseResolver) {
              videoPromiseResolver();
            }
            if (!inIntroductionRef.current) {
              setCanSelectOption(true);
              let idleEncouragementText = getCopyByGradeband(BLENDING_SELECTION_COPY, "idle_encouragement_1");
              if (selectedOption) {
                setSubmitEnabled(true);
                idleEncouragementText = getCopyByGradeband(BLENDING_SELECTION_COPY, "idle_encouragement_2");
              }

              resetIdleTimer(idleEncouragementText);
            }
          }}
          useButtonLabels={false}
          useClearButtons={true}
        />
      )}
      <div className={cx("imageSelectionWrapper", { hidden: hideOptions })}>
        <ImageSelection
          options={inIntroductionRef.current ? INTRODUCTION_MODEL_OPTIONS : options}
          handleSelection={(key) => canSelectOption && handleImageSelection(key)}
          useAnimation={true}
          animatedOptions={animatedOptions}
          selectedOption={selectedOption}
          useHover={canSelectOption}
          onImageLoadError={onImageLoadError}
          onAllImagesFailed={onAllImagesFailed}
        />
      </div>
      <AmiraIconButton
        type={ICON_BUTTON_TYPES.SUBMIT}
        oblong
        onClick={() => submitEnabled && handleSubmit()}
        disabled={!submitEnabled}
        invisible={hideSubmit}
        assessment={true}
      />
    </div>
  );
};

export default BlendingSelection;
